import { motion } from "framer-motion";
import PageHero from "@/components/layout/PageHero";
import CTA from "@/components/sections/CTA";
import { Lock, Server, ShieldCheck, KeyRound, FileSearch, Database, Globe2, HardDrive } from "lucide-react";
import { IMAGES } from "@/lib/constants";

const POSTURE = [
    {
        icon: Lock,
        label: "Encryption",
        summary: "Data encrypted in transit (TLS 1.3) and at rest (AES-256). Field-level encryption for PII and financial identifiers."
    },
    {
        icon: Server,
        label: "Hosting & Residency",
        summary: "India-hosted primary deployment on AWS Mumbai (ap-south-1) with restricted egress. EU / Singapore regional deployments available for regulated clients on request."
    },
    {
        icon: KeyRound,
        label: "Access Control",
        summary: "Role-based access control, SSO via SAML 2.0 / OIDC, mandatory MFA on all privileged accounts, least-privilege service tokens with 24-hour rotation."
    },
    {
        icon: FileSearch,
        label: "Audit Logging",
        summary: "Every read, write, and export is logged with actor, timestamp, and payload hash. Immutable audit trail available to client compliance and supervisory reviewers on request."
    },
    {
        icon: ShieldCheck,
        label: "Compliance Posture",
        summary: "ISO 27001 implementation in progress. SOC 2 Type II audit scoped for FY 2026-27. GDPR-aligned data processing terms for EU-facing engagements."
    },
    {
        icon: Database,
        label: "Zero-Copy Read Model",
        summary: "Carbonis is a read-only overlay. Client core banking / ERP data is not migrated. Where ingest is required, it is via signed, versioned files or scoped API tokens — never bulk exports."
    },
    {
        icon: Globe2,
        label: "Data Segregation",
        summary: "Every tenant is logically isolated with per-tenant encryption keys. No cross-tenant analytics without explicit written authorisation."
    },
    {
        icon: HardDrive,
        label: "Backups & Recovery",
        summary: "Encrypted backups every 6 hours with 30-day retention. RPO ≤ 6h · RTO ≤ 4h. Disaster-recovery drills quarterly."
    }
];

export default function Security() {
    return (
        <div data-testid="page-security">
            <PageHero
                title={<>Bank-grade posture, <em className="text-gold not-italic font-light">before the first line of code runs.</em></>}
                lead="Carbonis is engineered for institutional data — banks, regulators, and export-linked MSMEs. Every architectural decision starts with a compliance question, not a growth-hacking question."
                image={IMAGES.boardroom}
            />

            <section className="relative py-24 lg:py-32 border-t border-navy/10">
                <div className="max-w-[1500px] mx-auto px-6 lg:px-10">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-16 mb-16">
                        <div className="lg:col-span-6">
                            <div className="flex items-center gap-4 mb-6">
                                <div className="w-10 h-px bg-gold"></div>
                                <span className="font-mono text-[11px] tracking-[0.28em] uppercase text-gold">
                                    Data Governance
                                </span>
                            </div>
                            <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.02] tracking-[-0.02em] text-navy">
                                Eight commitments <em className="text-gold not-italic font-light">a CISO can sign off on.</em>
                            </h2>
                        </div>
                        <div className="lg:col-span-5 lg:col-start-8 lg:self-end">
                            <p className="text-navy/60 text-lg leading-relaxed">
                                The controls below are the standing answer to the first ten questions a bank compliance team asks. Full DPA, sub-processor list, and architecture diagram available on request.
                            </p>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-navy/10 border border-navy/10">
                        {POSTURE.map((p, i) => {
                            const Icon = p.icon;
                            return (
                                <motion.div
                                    key={p.label}
                                    initial={{ opacity: 0, y: 16 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true, margin: "-40px" }}
                                    transition={{ duration: 0.6, delay: i * 0.05 }}
                                    className="bg-paper p-7 lg:p-8 flex flex-col gap-4 min-h-[220px]"
                                    data-testid={`security-item-${p.label.toLowerCase().replace(/\s+/g, '-')}`}
                                >
                                    <div className="flex items-center justify-between">
                                        <Icon size={22} strokeWidth={1.3} className="text-gold" />
                                        <span className="font-mono text-[10px] tracking-[0.22em] uppercase text-navy/40">
                                            {String(i + 1).padStart(2, "0")}
                                        </span>
                                    </div>
                                    <h3 className="font-serif text-2xl leading-[1.15] tracking-[-0.01em] text-navy">
                                        {p.label}
                                    </h3>
                                    <p className="text-navy/60 text-[14px] leading-relaxed">
                                        {p.summary}
                                    </p>
                                </motion.div>
                            );
                        })}
                    </div>

                    <div className="mt-12 border-l-2 border-gold pl-6 py-2 max-w-[720px]">
                        <div className="font-mono text-[10px] tracking-[0.24em] uppercase text-gold mb-2">
                            Written Position
                        </div>
                        <p className="text-navy/70 text-[15px] leading-relaxed">
                            Carbonis does not — and will not — sell, license, or share tenant data with any third party, ever. Aggregated benchmarks are produced only from data with explicit written authorisation, and are always anonymised at the borrower and portfolio level.
                        </p>
                    </div>
                </div>
            </section>

            <CTA />
        </div>
    );
}
